import Button from 'components/Button/Button';
import { store } from "stores/Store";
import styles from './CheckInList.module.scss';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { date2usDotDateTime, date2usSlashDateTime } from 'helpers';
import { ReactComponent as ExpandIcon } from "assets/expand.svg";
import { CheckInItem } from 'types';
import { ReactComponent as CheckIcon } from "assets/verified.svg";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Button as Btn, Box, Center, Group, Loader, ScrollArea, Title } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import ReactSelect from 'react-select';
import useExternalApiService from 'helpers/ExternalApiService';
import Toastr from 'components/Toastr';

interface ICheckInListProps {
  checkIns: CheckInItem[] | undefined;
  onCheckInClick: (checkIn: CheckInItem) => void;
  addNewCheckIn: () => void;
  onSelectedAddressChange: (address: any) => void;
}

const CheckInList = ({checkIns, onCheckInClick, addNewCheckIn, onSelectedAddressChange}: ICheckInListProps) => {
  const [selectedAddress, setSelectedAddress] = useState<any>();
  const [checkInPhotos, setCheckInPhotos] = useState<{[key: number]: string[]}>({});
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(true);
  const apiUrl = process.env.REACT_APP_EAI_URL;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { patchData, putData, postData } = useExternalApiService(apiUrl);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    if (store.currentPractitioner?.addresses && store.currentPractitioner?.addresses.length > 0) {
      const defaultAddress = store.currentPractitioner.addresses.find(
        (a: any) => a.id === store.currentPractitioner.default_address
      );
      if (defaultAddress) {
        setSelectedAddress(defaultAddress);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.currentPractitioner]);

  useEffect(() => {
    if (selectedAddress) {
      onSelectedAddressChange(selectedAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress]);

  useEffect(() => {
    if (checkIns && checkIns.length > 0) {
      const fetchCheckInMiniatures = async () => {
        try {
          setIsLoadingPhotos(true);
          const result = await postData('/api/get_checkin_miniatures', {
            fmc_id: store.user?.userName || '',
            practitioner_id: checkIns[0].practitioner_id,
            checkin_ids: checkIns.map(checkIn => checkIn.id)
          });

          const photosMap: {[key: number]: string[]} = {};
          if (result?.checkins && typeof result.checkins === 'object') {
            Object.entries(result.checkins).forEach(([checkinId, url]) => {
              const id = parseInt(checkinId);
              photosMap[id] = url && typeof url === 'string' ? [url] : [];
            });
          }
          
          setCheckInPhotos(photosMap);
        } catch (error) {
          console.error('Failed to fetch check-in miniatures:', error);
          const photosMap: {[key: number]: string[]} = {};
          checkIns.forEach(checkIn => {
            photosMap[checkIn.id] = [];
          });
          setCheckInPhotos(photosMap);
        } finally {
          setIsLoadingPhotos(false);
        }
      };

      fetchCheckInMiniatures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIns]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleShowToast = (message: string) => {
    setShowToast(true);
    setToastMessage(message);
  };

  const handleAddNewCheckIn = () => {
    addNewCheckIn();
  }

  const renderCheckIn = (index: number, checkIn: CheckInItem) => {
    return (
      <div key={checkIn.id} className={styles.checkInItem}  onClick={() => onCheckInClick(checkIn)}>
        <div className={styles.checkInItemContent}>
        <div className={styles.checkInItemHeader}>
          <p className={styles.checkinNumber}>{index + 1}. </p>
          {/*<p className={styles.paragraph}>{date2usSlashDateTime(checkIn.created_at)}</p>*/}
          
          <p className={styles.paragraph}>
            {date2usSlashDateTime(
              checkIn.follow_up_date ?? checkIn.created_at,
              checkIn.is_archived
                ? 'GMT'
                : checkIn.is_archived_bg
                ? 'America/New_York'
                : undefined
            )}
          </p>
          <CheckIcon className={`${styles.checkIcon} ${!checkIn.verified && styles.unverified}`} />
          {checkIn.status === "draft" && (
            <span className={styles.draftBadge}>DRAFT</span>
          )}

          {checkIn?.is_local && (
            <span className={styles.localBadge}>OFFLINE</span>
          )}

          {checkIn.is_archived && (
            <span className={styles.exportedBadge}>IMPORTED</span>
          )}
          {checkIn.is_archived_bg && (
            <span className={styles.exportedBadge}>IMPORTED</span>
          )}
        </div>
        <p className={styles.fmcName}>{checkIn.user_name}</p>
        <p className={styles.notes}>
          {checkIn.notes && checkIn.notes.length > 150 ? `${checkIn.notes.slice(0, 150)}...` : checkIn.notes}
        </p>
        </div>
        <div className={styles.photoContainer}>
          {checkInPhotos[checkIn.id] && checkInPhotos[checkIn.id].length > 0 ? (
            <img
              src={checkInPhotos[checkIn.id][0]}
              alt={`Check-in ${checkIn.id}`}
              className={styles.photoImage}
            />
          ) : (
            isLoadingPhotos ? (
              <div className={styles.noPhotoPlaceholder}>
                <span className={styles.noPhotoText}>Loading...</span>
              </div>
            ) : (
              null
            )
          )}
        </div>
        <div className={styles.iconWrapper}>          
          <ExpandIcon/>
        </div>
      </div>
    )
  }

  return(
    <div className={styles.wrapper}>
      <Toastr 
        backgroundColor="white" 
        message={toastMessage} 
        isVisible={showToast} 
        onClose={() => setShowToast(false)} 
      />
      <div className={styles.headerWrapper}>
        You have a total of {checkIns?.length || 0} check-ins
        <Button textColor="white" color="#00C542" borderColor="#00C54280" type="outline" onClick={() => handleAddNewCheckIn()}>CHECK-IN</Button>
      </div>
        <Box>
          <Group justify="space-between" my="md">
            <Box c="dimmed">Default Check-in Address</Box>
            {store.currentPractitioner?.default_address !== selectedAddress?.id ? (
              <Btn
                h={20}
                bg="#4F7D9C"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  putData(`/api/add_practitioner_to_chat/${store.currentPractitioner?.id}`, {
                    practitioner_data: {
                      default_address: selectedAddress?.id
                    }
                  });
                  store.clearPractitionersCache();
                  store.setCurrentPractitioner({
                    ...store.currentPractitioner,
                    default_address: selectedAddress?.id
                  });
                }}>
                SET
              </Btn>
            ) : null }
          </Group>
          <ReactSelect
            value={selectedAddress}
            onChange={(v: any) => {
              if (v?.value) {
                const a = store.currentPractitioner.addresses.find((a: any) => a.id === v.value);
                if (a) {
                  setSelectedAddress(a);
                }
              }
            }}
            placeholder="Select an Address"
            components={{
              Control: (e) => {
                return (
                  <Group
                    style={{border: "1px solid #ddd", borderRadius: "10px"}}
                    align="center"
                    onClick={() => {
                      if (e.selectProps.menuIsOpen) {
                        e.selectProps.onMenuClose();
                      } else {
                        e.selectProps.onMenuOpen();
                      }
                    }}>
                    {e.children}
                  </Group>
                )
              },
              SingleValue: (e) => {
                const containerRef = useRef();

                const handleClickOutside = (event: any) => {
                  if (containerRef.current && !(containerRef.current as HTMLElement).contains(event.target)) {
                    e.selectOption(e.data);
                  }
                }

                useEffect(() => {
                  document.addEventListener('mousedown', handleClickOutside);
                  return () => {
                    document.removeEventListener('mousedown', handleClickOutside);
                  };
                  // eslint-disable-next-line react-hooks/exhaustive-deps
                }, []);

                return (
                  <Box
                    w="100%"
                    mt={0}
                    ref={containerRef as any}>
                    <Title order={5} w="100%" c="dark">
                      {selectedAddress?.is_visiting_address ? (
                        `VISITING ADDRESS ${e.options
                          .filter((oo) => store.currentPractitioner?.addresses?.find((a: any) => a.id === oo.value)?.is_visiting_address)
                          .findIndex((oo: any) => oo.value === selectedAddress.id) + 1}`
                      ) : (
                        `SHIPPING/BILLING ADDRESS`
                      )}
                    </Title>
                  </Box>
                )
              },
              Input: (e) => {
                return <></>;
              },
              MenuList: (o) => {
                return (
                  <Box mah={300} style={{overflowY: "scroll"}} >
                    {o.children}
                  </Box>
                )
              },
              Option: (o) => {
                const address = store.currentPractitioner.addresses.find((a: any) => a.id === (o as any).value);
                if (!address) return <></>;

                return (
                  <Box
                    w="95%"
                    my={5}
                    style={{
                      borderBottom: "1px solid #eee",
                      padding: "10px",
                    }}
                    bg={(o as any)?.value === selectedAddress?.id ? "#E3F4FF" : "auto"}
                    onClick={() => o.selectOption(o.data)}>
                    <Title order={5} w="100%" c="dark" mb={2}>
                      {address?.is_visiting_address ? (
                        `VISITING ADDRESS ${o.options
                          .filter((oo) => store.currentPractitioner?.addresses?.find((a: any) => a.id === oo.value)?.is_visiting_address)
                          .findIndex((oo) => oo.value === (o as any).value) + 1}`
                      ) : (
                        `SHIPPING ADDRESS`
                      )}
                    </Title>
                    <Box pos="relative">
                      <Title order={6} fw="normal" c="gray">
                        {address.address}
                        <br />
                        {address.phone}
                      </Title>
                    </Box>
                  </Box>
                );
              }
            }}
            styles={{
              control: (baseStyles: any) => ({
                ...baseStyles,
                border: 'none',
                borderBottom: '1px solid #ccc',
                paddingLeft: 0,
                marginLeft: 0,
                color: '#4F7D9C',
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 'none'
                }
              }),
              valueContainer: (baseStyles: any) => ({
                ...baseStyles,
                paddingLeft: 0,
                marginLeft: 10,
                color: '#4F7D9C',
              }),
              indicatorSeparator: () => ({
                display: 'none'
              }),
              menu: (baseStyles: any) => ({
                ...baseStyles,
                zIndex: 999,
              }),
            }}
            options={store.currentPractitioner.addresses
              ?.slice()
              ?.sort((a1: any, a2: any) => a1.id - a2.id)
              .map((a: any) => ({
                label: a.address,
                value: a.id
              }))}
          />
        </Box>
      {!checkIns ? (
        <Center>
          <Loader />
        </Center>
      ): (
        <>
        {/* Warning message if any check-in is archived */}
        {checkIns.some(checkIn => checkIn.is_archived || checkIn.is_archived_bg) && (
          <div className={styles.warningMessage}>
            Imported check-in's may contain wrong times & duplicates.
          </div>
        )}

        <div className={styles.checkInListWrapper}>
          {checkIns.map((checkIn, index) => {
            return renderCheckIn(index, checkIn)
          })}  
        </div>
        </>
      )}

      
    </div>
  )
}

export default CheckInList;